/**
 * User store slice for user preferences, profile, and settings management
 * Manages extended user data beyond basic authentication
 */

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { UserStore, UserPreferences, UserProfile, UserSettings } from '../types'
import { devtoolsDev } from '../middleware'

/**
 * Default user preferences
 */
const defaultPreferences: UserPreferences = {
  language: 'en',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  dateFormat: 'MM/DD/YYYY',
  emailNotifications: true,
  pushNotifications: true,
  twoFactorEnabled: false
}

/**
 * Default user settings
 */
const defaultSettings: UserSettings = {
  autoSave: true,
  compactMode: false,
  showTips: true,
  analyticsEnabled: true
}

/**
 * Initial user state
 */
const initialUserState = {
  preferences: defaultPreferences,
  profile: null,
  settings: defaultSettings
}

/**
 * Creates the user store with actions
 */
const createUserStore = () => create<UserStore>()(
  devtoolsDev(
    // @ts-ignore
    persist(
      immer((set) => ({
        // Initial state
        ...initialUserState,

        // Preferences actions
        updatePreferences: (preferences: Partial<UserPreferences>) => {
          set((state) => {
            state.preferences = { ...state.preferences, ...preferences }
          })
        },

        resetPreferences: () => {
          set((state) => {
            state.preferences = defaultPreferences
          })
        },

        // Profile actions
        updateProfile: (profile: Partial<UserProfile>) => {
          set((state) => {
            if (state.profile) {
              state.profile = { ...state.profile, ...profile }
            } else {
              // Create new profile if none exists
              state.profile = {
                id: profile.id || '',
                name: profile.name || '',
                email: profile.email || '',
                ...profile
              }
            }
          })
        },

        // Settings actions
        updateSettings: (settings: Partial<UserSettings>) => {
          set((state) => {
            state.settings = { ...state.settings, ...settings }
          })
        },

        resetSettings: () => {
          set((state) => {
            state.settings = defaultSettings
          })
        }
      })),
      {
        name: 'taqat-user-store',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          preferences: state.preferences,
          settings: state.settings
          // Profile data should not be persisted - it should come from database
          // Only persist user preferences and settings (non-sensitive local config)
        })
      }
    ),
    'UserStore'
  )
)

/**
 * User store instance
 */
export const userStore = createUserStore()

/**
 * User store hook for components
 * @returns User store state and actions
 */
export const useUserStore = () => userStore()

/**
 * Hook for user preferences management
 * @returns User preferences and preference actions
 */
export const useUserPreferences = () => {
  const { preferences, updatePreferences, resetPreferences } = useUserStore()
  
  return {
    preferences,
    updatePreferences,
    resetPreferences,
    // Convenience methods for specific preferences
    setLanguage: (language: string) => updatePreferences({ language }),
    setTimezone: (timezone: string) => updatePreferences({ timezone }),
    setDateFormat: (dateFormat: string) => updatePreferences({ dateFormat }),
    toggleEmailNotifications: () => updatePreferences({ emailNotifications: !preferences.emailNotifications }),
    togglePushNotifications: () => updatePreferences({ pushNotifications: !preferences.pushNotifications }),
    toggleTwoFactor: () => updatePreferences({ twoFactorEnabled: !preferences.twoFactorEnabled })
  }
}

/**
 * Hook for user profile management
 * @returns User profile and profile actions
 */
export const useUserProfile = () => {
  const { profile, updateProfile } = useUserStore()
  
  return {
    profile,
    updateProfile,
    // Convenience methods for profile updates
    updateBasicInfo: (name: string, bio?: string) => updateProfile({ name, bio }),
    updateContactInfo: (email: string, website?: string) => updateProfile({ email, website }),
    updateWorkInfo: (company?: string, location?: string) => updateProfile({ company, location }),
    updateSocialLinks: (socialLinks: UserProfile['socialLinks']) => updateProfile({ socialLinks }),
    clearProfile: () => updateProfile({ 
      bio: undefined, 
      company: undefined, 
      location: undefined, 
      website: undefined, 
      socialLinks: undefined 
    })
  }
}

/**
 * Hook for user settings management
 * @returns User settings and settings actions
 */
export const useUserSettings = () => {
  const { settings, updateSettings, resetSettings } = useUserStore()
  
  return {
    settings,
    updateSettings,
    resetSettings,
    // Convenience methods for specific settings
    toggleAutoSave: () => updateSettings({ autoSave: !settings.autoSave }),
    toggleCompactMode: () => updateSettings({ compactMode: !settings.compactMode }),
    toggleShowTips: () => updateSettings({ showTips: !settings.showTips }),
    toggleAnalytics: () => updateSettings({ analyticsEnabled: !settings.analyticsEnabled })
  }
}

/**
 * Hook for complete user data
 * @returns All user state and actions
 */
export const useUser = () => {
  const { 
    preferences, 
    profile, 
    settings, 
    updatePreferences, 
    updateProfile, 
    updateSettings, 
    resetPreferences, 
    resetSettings 
  } = useUserStore()
  
  return {
    preferences,
    profile,
    settings,
    updatePreferences,
    updateProfile,
    updateSettings,
    resetPreferences,
    resetSettings
  }
}

/**
 * Selector hooks for specific data
 */
export const useLanguage = () => {
  return userStore((state) => state.preferences.language)
}

export const useTimezone = () => {
  return userStore((state) => state.preferences.timezone)
}

export const useNotificationSettings = () => {
  return userStore((state) => ({
    emailNotifications: state.preferences.emailNotifications,
    pushNotifications: state.preferences.pushNotifications
  }))
}

export const useCompactMode = () => {
  return userStore((state) => state.settings.compactMode)
}

export const useAnalyticsEnabled = () => {
  return userStore((state) => state.settings.analyticsEnabled)
}

/**
 * Utility function to check if user has completed profile setup
 * @returns Boolean indicating if profile is complete
 */
export const useIsProfileComplete = () => {
  return userStore((state) => {
    const profile = state.profile
    return !!(
      profile &&
      profile.name &&
      profile.email &&
      profile.bio
    )
  })
}

/**
 * Utility function to get user's display name
 * @returns User's display name or fallback
 */
export const useDisplayName = () => {
  return userStore((state) => {
    const profile = state.profile
    return profile?.name || 'User'
  })
}